import * as React from 'react';
import { Button, IModalProps, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { PriceLookupContext, ProjectionDomain, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { GetProductUnitOfMeasureInput, getProductUnitsOfMeasureAction } from '../../../../actions/getproductunitsofmeasure.action';
import { getActivePricesAction, GetActivePricesInput } from '../../../../actions/getactiveprices.action';

interface IOrderListShare {
    isOpen: boolean;
    setIsOpen: (val: boolean) => void;
    onConfirm: (data: { productId?: number; quantity: number; unitOfMeasure: string }) => void;
    context: ICoreContext;
    productId?: number;
    product?: SimpleProduct;
    imageSettings?: IImageSettings;
    unitPrice?: number;
}

interface IUnitOfMeasureOption {
    unitOfMeasureDropDownName: string;
    unitOfMeasureDropDownValue: string;
}

interface IAlternateQuantityProps {
    quantity: number;
    setQuantity: (value: number) => void;
    min?: number;
    max?: number;
}

const AlternateProductQuantity: React.FC<IAlternateQuantityProps> = ({ quantity, setQuantity, min = 1, max = 999 }) => {
    const onIncrementalChange = (amount: number) => () => {
        const nextValue = quantity + amount;
        if (nextValue >= min && nextValue <= max) {
            setQuantity(nextValue);
        }
    };

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = Number.parseInt(event.target.value, 10);

        if (Number.isNaN(nextValue)) {
            setQuantity(min);
            return;
        }

        if (nextValue > max) {
            setQuantity(max);
            return;
        }

        if (nextValue < min) {
            setQuantity(min);
            return;
        }

        setQuantity(nextValue);
    };

    const onBlur = () => {
        if (quantity < min) {
            setQuantity(min);
        } else if (quantity > max) {
            setQuantity(max);
        }
    };

    const decrementDisabled = quantity <= min;
    const incrementDisabled = quantity >= max;

    return (
        <div className='modal-quantity'>
            <div className='quantity' id='ms-buybox__product-quantity-input'>
                <button
                    disabled={decrementDisabled}
                    title={decrementDisabled ? '' : 'Press to decrease the quantity by 1'}
                    className={`decrement quantity__controls ${decrementDisabled ? 'disabled' : ''}`}
                    aria-hidden={true}
                    aria-label='Press to decrease the quantity by 1'
                    color='secondary'
                    onClick={onIncrementalChange(-1)}
                    type='button'
                >
                    <span className='fas fa-minus quantity__controls-glyph' />
                </button>
                <input
                    type='number'
                    className='quantity-input'
                    pattern='[0-9]*'
                    value={quantity}
                    onChange={onInputChange}
                    onBlur={onBlur}
                    aria-live='polite'
                    aria-label='quantity input'
                    role='spinbutton'
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={quantity}
                    min={min}
                    max={max}
                />
                <button
                    disabled={incrementDisabled}
                    title='Press to increment quantity by 1'
                    className={`increment quantity__controls ${incrementDisabled ? 'disabled' : ''}`}
                    aria-hidden={false}
                    aria-label='Press to increment quantity by 1'
                    color='secondary'
                    onClick={onIncrementalChange(1)}
                    type='button'
                >
                    <span className='fas fa-plus quantity__controls-glyph' />
                </button>
            </div>
        </div>
    );
};

const OrderListSupercedAddAlternateConfirmation = (props: IOrderListShare) => {
    const { isOpen, setIsOpen, onConfirm, context, productId, product, imageSettings, unitPrice = 0 } = props;
    const [quantity, setQuantity] = React.useState(1);
    const [unitOptions, setUnitOptions] = React.useState<IUnitOfMeasureOption[]>([]);
    const [selectedUnit, setSelectedUnit] = React.useState('');
    const [resolvedProduct, setResolvedProduct] = React.useState<SimpleProduct | undefined>();
    const [resolvedUnitPrice, setResolvedUnitPrice] = React.useState(unitPrice);
    const [loading, setLoading] = React.useState<Boolean>(true);

    const {
        actionContext,
        request: { app, gridSettings }
    } = context;
    const {
        requestContext: { apiSettings }
    } = actionContext;

    const modalPropsTerms: IModalProps = {
        applicationNode: MsDyn365.isBrowser ? document.body : '',
        isOpen,
        autoFocus: true,
        size: 'sm',
        keyboard: true,
        backdrop: true,
        zIndex: 1000,
        fade: true,
        // className: 'share-orderlist-modal',
        className: 'order-list-supersed-confirmation',
        toggle: () => setIsOpen(false)
    };

    React.useEffect(() => {
        const loadProduct = async () => {
            if (!productId || !isOpen) {
                // setResolvedProduct(product);
                // setResolvedUnitPrice(unitPrice);
                return;
            }

            try {
                const fetchedProduct = await getByIdAsync(
                    { callerContext: actionContext },
                    productId,
                    context.request.apiSettings.channelId
                );

                if (fetchedProduct) {
                    setResolvedProduct(fetchedProduct as SimpleProduct);
                    setResolvedUnitPrice(fetchedProduct.Price ?? unitPrice);
                    setLoading(false);
                    return;
                }
            } catch (error) {
                console.log('Failed to fetch alternate product by id', error);
            }

            setResolvedProduct(product);
            setResolvedUnitPrice(unitPrice);
        };

        void loadProduct();
    }, [actionContext, context.request.apiSettings.channelId, isOpen, product, productId, unitPrice]);

    React.useEffect(() => {
        if (isOpen && resolvedProduct && resolvedProduct.RecordId && selectedUnit) {
            const getActivePricesInputMeasure: PriceLookupContext | null = {
                HeaderContext: undefined,
                LineContexts: [
                    {
                        ProductRecordId: resolvedProduct.RecordId,
                        UnitOfMeasureSymbol: selectedUnit
                    }
                ]
            };
            const projectDomain: ProjectionDomain = {
                ChannelId: context.request.apiSettings.channelId,
                CatalogId: context.request.apiSettings.catalogId
            };
            const getActivePricesInput = new GetActivePricesInput(
                projectDomain,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                false,
                false,
                getActivePricesInputMeasure
            );

            getActivePricesAction(getActivePricesInput, actionContext).then(res => {
                if (res && res[0]) {
                    setResolvedUnitPrice(res[0]?.CustomerContextualPrice ?? 0);
                }
            });
        }
    }, [isOpen, resolvedProduct, selectedUnit]);

    React.useEffect(() => {
        const loadUnits = async () => {
            if (!productId || !isOpen || !resolvedProduct?.RecordId) {
                return;
            }

            const input = new GetProductUnitOfMeasureInput(resolvedProduct.RecordId, {
                IncludeQuantityLimitRules: true
            });
            const result = await getProductUnitsOfMeasureAction(input, actionContext);
            const filteredData =
                result
                    ?.filter((item: any) => item.Description === 'Case' || item.Description === 'Each')
                    .map((item: any) => ({
                        unitOfMeasureDropDownName: item.Description,
                        unitOfMeasureDropDownValue: item.Symbol
                    })) ?? [];

            setUnitOptions(filteredData);

            if (filteredData.length) {
                const matchingOption = filteredData.find(
                    item =>
                        item.unitOfMeasureDropDownValue === resolvedProduct?.DefaultUnitOfMeasure ||
                        item.unitOfMeasureDropDownName === resolvedProduct?.DefaultUnitOfMeasure
                );
                setSelectedUnit((matchingOption ?? filteredData[0]).unitOfMeasureDropDownValue);
            } else {
                setSelectedUnit(resolvedProduct?.DefaultUnitOfMeasure ?? '');
            }
        };

        void loadUnits();
    }, [actionContext, isOpen, resolvedProduct?.RecordId, resolvedProduct?.DefaultUnitOfMeasure]);

    React.useEffect(() => {
        if (!isOpen) {
            setQuantity(1);
        }
    }, [isOpen]);

    const formatPrice = (price: number): string => {
        const {
            cultureFormatter: {
                formatCurrency,
                // @ts-expect-error
                currencyCode
            }
        } = context;

        return formatCurrency(price.toFixed(2), currencyCode);
    };

    const selectedUnitLabel =
        unitOptions.find(option => option.unitOfMeasureDropDownValue === selectedUnit)?.unitOfMeasureDropDownName ||
        resolvedProduct?.DefaultUnitOfMeasure ||
        '';
    const totalPrice = resolvedUnitPrice * quantity;
    const fallbackImage = getFallbackImageUrl(resolvedProduct?.ItemId, apiSettings, app?.config?.OmniChannelMedia) ?? '';

    return (
        <Modal {...modalPropsTerms}>
            <ModalHeader closeAriaLabel='Close' closeButtonLabel='Close' toggle={() => setIsOpen(false)} />
            <div className='title'>Add Alternate Item to Order List</div>
            {loading ? (
                <></>
            ) : (
                <>
                    <ModalBody className='body'>
                        <div className='confirmation-text'>
                            You have selected an alternate item to replace the current item. Are you sure you want to proceed and add it to
                            your order list?
                        </div>
                        <div className='wrapper'>
                            <div className='product-image'>
                                <Image
                                    altText={resolvedProduct?.Name ?? 'alternate product'}
                                    requestContext={actionContext.requestContext}
                                    src={resolvedProduct?.PrimaryImageUrl ?? ''}
                                    fallBackSrc={fallbackImage}
                                    className='thumbnail'
                                    imageSettings={imageSettings}
                                    gridSettings={gridSettings!}
                                    loadFailureBehavior='empty'
                                />
                            </div>
                            <div className='product-info'>
                                <div className='id'>{resolvedProduct?.ItemId}</div>
                                <div className='name'>{resolvedProduct?.Name}</div>
                                <div className='price'>Unit price: {formatPrice(resolvedUnitPrice)}</div>
                                <div className='uom'>UOM: {selectedUnitLabel}</div>
                            </div>
                            <div className='uom-selector'>
                                <select
                                    id='alternate-product-unit-of-measure'
                                    value={selectedUnit}
                                    onChange={event => setSelectedUnit(event.target.value)}
                                >
                                    {unitOptions.map(unit => (
                                        <option key={unit.unitOfMeasureDropDownValue} value={unit.unitOfMeasureDropDownValue}>
                                            {unit.unitOfMeasureDropDownName}
                                        </option>
                                    ))}
                                </select>
                                <div className='quantity-text'>Quantity</div>
                                <AlternateProductQuantity quantity={quantity} setQuantity={setQuantity} />
                                <div className='total-wrapper'>
                                    <span className='total'>Total</span>
                                    <span className='price'>{formatPrice(totalPrice)}</span>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter style={{ justifyContent: 'flex-end' }}>
                        {/* <Button className='button' onClick={() => setIsOpen(false)}>
                    Cancel
                </Button> */}
                        <Button
                            className={`button`}
                            onClick={() => {
                                if (resolvedProduct && resolvedProduct.RecordId) {
                                    onConfirm({
                                        productId: resolvedProduct.RecordId,
                                        quantity,
                                        unitOfMeasure: selectedUnit || resolvedProduct?.DefaultUnitOfMeasure || ''
                                    });
                                }
                                setIsOpen(false);
                            }}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
};

export default OrderListSupercedAddAlternateConfirmation;
